import React, { useEffect, useState } from 'react';
import { Button, LocaleText, Table } from 'ui';
import { useIntl } from 'react-intl';
import { Checkbox, Divider, message, Popover } from 'antd';
import generalHelpers from 'utils/generalHelpers';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';
import coursesApiClient from 'utils/coursesApiClient';
import FileDownload from 'js-file-download';

const defaultFields: string[] = [
    'audienceTitle',
    'audienceUsers',
    'competenceBlockTitles',
    'competenceTitles',
    'programTitles',
];

type LocaleTextMap = Record<string, string>;

const renderJsonArr = (jsonArr: string | null, useLocale = false) => {
    const titleArr: Array<string | LocaleTextMap> = jsonArr ? JSON.parse(jsonArr) : [];

    if (titleArr.length) {
        return (
            <>
                {titleArr.map((title, idx) => (
                    <p key={typeof title === 'string' ? title : Object.keys(title).sort().join('|') || idx}>
                        {useLocale ? (
                            <LocaleText text={typeof title === 'string' ? undefined : title} defaultText={typeof title === 'string' ? title : ''} />
                        ) : (
                            typeof title === 'string' ? title : Object.values(title)[0] ?? '-'
                        )}
                    </p>
                ))}
            </>
        );
    } else {
        return '-';
    }
};

const cacheKey = 'sessionColumns_AUDIENCE';

// Saved column selection; missing or empty means the user never saved one,
// so every column is shown by default.
const readCachedColumns = (): string[] | null => {
    const cached = localStorage.getItem(cacheKey);

    return cached ? cached.split(',') : null;
};

const AudienceReport: React.FC<any> = ({ filter, load, setLoad, form }) => {
    const [reload, setReload] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [selectedColumns, setSelectedColumns] = useState<CheckboxValueType[]>(
        () => readCachedColumns() ?? defaultFields,
    );
    const [openPopover, setOpenPopover] = useState(false);
    const [displayColumns, setDisplayColumns] = useState<any>(() => readCachedColumns() ?? defaultFields);

    const intl = useIntl();

    useEffect(() => {
        if (filter !== null && load) {
            filterSession();
            setLoad(false);
        }
    }, [load]);

    const filterSession = () => {
        setReload(true);
    };

    const columns = [
        {
            title: intl.formatMessage({ id: 'general.audience_title' }),
            dataIndex: 'audienceTitle',
            key: 'audienceTitle',
            sorter: true,
        },
        {
            title: intl.formatMessage({ id: 'general.audience_users' }),
            dataIndex: 'audienceUsers',
            key: 'audienceUsers',
            sorter: true,
        },
        {
            title: intl.formatMessage({ id: 'general.competence_block_title' }),
            dataIndex: 'competenceBlockTitles',
            key: 'competenceBlockTitles',
            sorter: true,
            render: (competenceBlockTitles: string) => renderJsonArr(competenceBlockTitles),
        },
        {
            title: intl.formatMessage({ id: 'general.competence_title' }),
            dataIndex: 'competenceTitles',
            key: 'competenceTitles',
            sorter: true,
            render: (competenceTitles: string) => renderJsonArr(competenceTitles),
        },
        {
            title: intl.formatMessage({ id: 'general.program_title' }),
            dataIndex: 'programTitles',
            key: 'programTitles',
            sorter: true,
            render: (programTitles: string) => renderJsonArr(programTitles, true),
        },
    ];
    const content = (
        <div>
            <Checkbox.Group
                value={selectedColumns}
                onChange={(checkboxGroupColumns: CheckboxValueType[]) =>
                    setSelectedColumns(checkboxGroupColumns)
                }
            >
                {Object.values(columns).map((el: any) => {
                    return (
                        <div key={el.dataIndex} className={'flex p-1 gap-5'}>
                            <Checkbox key={el.dataIndex} value={el.dataIndex} />
                            {el.title}
                        </div>
                    );
                })}
            </Checkbox.Group>
            <Divider plain className={'m-2'} />
            <div className={'flex justify-between p-1'}>
                <Button type={'link'} onClick={() => handleCancel()} message='general.cancel' />
                <Button type={'primary'} onClick={() => handleColumnSubmit()} message='general.select' />
            </div>
        </div>
    );

    const handleColumnSubmit = () => {
        setDisplayColumns(selectedColumns);
        localStorage.setItem(cacheKey, selectedColumns.toString());
        setOpenPopover(false);
    };

    const handleCancel = () => {
        setSelectedColumns(readCachedColumns() ?? defaultFields);
        setOpenPopover(false);
    };

    const exportData = async () => {
        const values = form.getFieldsValue();
        const query = generalHelpers.filterParser(values).replace('&', '?');

        setExportLoading(true);
        try {
            const url = '/api/v1/courses/reports/export/AUDIENCE' + query;
            const response = await coursesApiClient.request(url, [], 'GET', true, true);

            if (response.fileName) {
                FileDownload(response, response.fileName);
            } else {
                message.error(intl.formatMessage({ id: 'error.data_load' }));
            }
        } catch (error) {
            message.error(intl.formatMessage({ id: 'error.data_load' }));
        } finally {
            setExportLoading(false);
        }
    };

    return (
        <div className={'reports-element'}>
            <div className={'flex justify-between'}>
                <Button loading={exportLoading} type={'outline'} onClick={() => exportData()} message='general.export' />
                <Popover
                    open={openPopover}
                    placement="bottomRight"
                    title={intl.formatMessage({ id: 'general.columns' })}
                    content={content}
                    trigger="click"
                >
                    <Button type={'outline'} onClick={() => setOpenPopover(!openPopover)} message=' ' icon='fa-solid fa-table-columns' />
                </Popover>
            </div>
            <Table
                columns={columns.filter((el: any) => displayColumns.includes(el.dataIndex))}
                url={'/api/v1/courses/reports/audiences'}
                reload={reload}
                setReload={setReload}
                rowKey={'id'}
                filter={filter}
                scroll={{ x: true }}
                rowSelection={false}
                hasCustomFields={false}
                fetchOnLoad={false}
                showTotalResults={true}
            />
        </div>
    );
};

export default AudienceReport;
